import { useLocale } from '@mirai/locale';
import { Notification, Progress, styles, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { DEFAULT_LOCALE, SCRIPT_ID, SCRIPT_URL, SUPPORTED_LOCALES } from './FormMews.constants';
import { L10N } from './FormMews.l10n';
import * as style from './FormMews.module.css';
import { getStyle } from './helpers';
import { loadScript } from '../../../../../helpers/loadScript';
import { trackError } from '../../../../../helpers/trackError';

export const FormMews = ({ response, ...others }) => {
  const { locale, translate } = useLocale();

  const [error, setError] = useState();
  const [ready, setReady] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);

  const ref = useRef();

  useEffect(() => {
    let mounted = true;
    let cleanup;

    (async () => {
      try {
        cleanup = await loadScript({
          async: false,
          domain: 'payments',
          id: SCRIPT_ID,
          src: SCRIPT_URL,
          onLoad: () => setReady(true),
        });
        if (mounted && window.Mews?.PaymentCheckout) setReady(true);
      } catch (nextError) {
        setError(nextError);
      }
    })();

    return () => {
      mounted = false;
      cleanup && cleanup();
      window?.Mews?.PaymentCheckout?.destroy();
    };
  }, []);

  useEffect(() => {
    if (!ready || !response) return;

    const { configuration: { requestId, dataBaseUrl } = {} } = response;
    try {
      setError();
      setPaymentReady(false);
      window.Mews.PaymentCheckout.load({
        containerId: ref.current?.id,
        dataBaseUrl,
        enableTracking: true,
        languageCode: SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE,
        layout: { hidden: { surchargeSummary: true, paymentMethodHeader: true } },
        requestId,
        styles: getStyle(),
        onSuccess: handleSuccess,
        onFailure: handleFailure,
      });
      setPaymentReady(true);
    } catch (nextError) {
      setError(nextError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  const handleFailure = (result) => {
    setError(result);
    if (result) trackError('Mews', result instanceof Error ? result.stack : result?.error);
  };

  const handleSuccess = (result) => {
    if (!result) return;

    const { configuration: { paymentTransactionId, returnUrl } = {} } = response;
    const { paymentMethodId } = result;
    const url = new URL(returnUrl);
    paymentTransactionId && url.searchParams.append('paymentTransactionId', paymentTransactionId);
    paymentMethodId && url.searchParams.append('paymentMethodId', paymentMethodId);

    window.location.href = url.toString();
  };

  return (
    <View {...others} className={styles(others.className, style.container)}>
      <Progress indeterminate visible={!paymentReady && !error} />
      <View id="Mews-checkout" ref={ref} className={style.checkout} />
      {error && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR)}
        </Notification>
      )}
    </View>
  );
};

FormMews.displayName = 'Mirai:Payments:ButtonPayment:FormMews';

FormMews.propTypes = {
  response: PropTypes.shape(),
};
